/**
 * The one way a Resource's Account reservation is shown (docs/specs/resource-reservation.md):
 * directory rows, the profile header, picker candidates, the schedule, and plan rows.
 *
 * `forThisAccount` flips the copy for surfaces scoped to one Account's work — a Resource
 * reserved for the Account you are staffing reads as a match, not a warning.
 */

import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";

/** "2026-12-31" → "31 Dec 2026". Parsed as UTC so the day never shifts by timezone. */
export function formatReservationDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Hover / screen-reader sentence for a reservation, e.g. "Reserved for Acme until 31 Dec 2026". */
export function reservationSentence(accountName: string, until: string | null): string {
  return until === null
    ? `Reserved for ${accountName}, no end date`
    : `Reserved for ${accountName} until ${formatReservationDay(until)}`;
}

interface Props {
  accountName: string;
  until: string | null;
  /** True when the surface is staffing this same Account's work. */
  forThisAccount?: boolean;
  className?: string;
}

export function ReservationBadge({ accountName, until, forThisAccount = false, className }: Props) {
  const label = forThisAccount ? "Reserved for this Account" : `Reserved · ${accountName}`;
  return (
    <span
      title={reservationSentence(accountName, until)}
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-md bg-[var(--reserved-bg)] px-1.5 py-0.5 text-xs font-semibold text-[var(--reserved)]",
        className,
      )}
    >
      <Lock className="size-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
}
